import { States } from '@/helpers/common/states';
import api, { AxiosError } from '@/services/api';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Loading } from '@/components';
import { EventFractionalMainReport } from '@/model/EventFractionalMainReport';
import { EventFractionalOrderReport } from '@/model/EventFractionalOrderReport';
import { useDialog } from '@/hooks/useDialog';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { unmask, updateMask } from '@/helpers/masks/cashNumber';
import { CreateAdmOrderPayment } from '@/model/CreateAdmOrderPayment';
import { FormInputName, ShouldShowModal } from './types';
import { EventFractionalReportUI } from './ui';

interface EventFractionalReportProps {
  eventId: string;
}

export const EventFractionalReportScreen: React.FC<EventFractionalReportProps> = ({ eventId }) => {
  const [state, setState] = useState<States>(States.default);
  const [report, setReport] = useState<EventFractionalMainReport>();
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.DETAIL);
  const [selectedOrder, setSelectedOrder] = useState<EventFractionalOrderReport>();

  const { title, visible, onChangeTitle, onToggle } = useDialog();

  const getEventFractionls = async (): Promise<void> => {
    if (eventId) {
      try {
        setState(States.loading);
        const { data } = await api.get<EventFractionalMainReport>(
          `event/report/${eventId}/fractional`,
        );
        if (data) {
          setReport(data);
        }
      } catch (error) {
        const err = error as AxiosError;
        toast.error(err.message);
      } finally {
        setState(States.default);
      }
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      orderId: '',
      ticket: '',
      label: '',
      value: '',
      paymentType: '',
    },
    validators: {
      orderId: [validators.required],
      ticket: [validators.required],
      label: [validators.required],
      value: [validators.required],
      paymentType: [validators.required],
    },
    formatters: {
      value: updateMask,
    },
  });

  const handleOnShouldShowModal = ({
    value,
    newTitleModal,
    order,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    order: EventFractionalOrderReport;
  }): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    if (value === ShouldShowModal.DETAIL) {
      setSelectedOrder(order);
    } else {
      onChangeFormInput(FormInputName.orderId)(order.orderId);
      onChangeFormInput(FormInputName.ticket)(order.ticket);
      onChangeFormInput(FormInputName.label)(`${order.label}`);
    }
    onToggle();
  };

  const handleOnCreatePayment = async (): Promise<void> => {
    if (isFormValid()) {
      try {
        setState(States.loading);
        const orderId = formData[FormInputName.orderId];
        const payload: CreateAdmOrderPayment = {
          value: Number(unmask(formData[FormInputName.value])),
          paymentType: Number(formData[FormInputName.paymentType]),
        };
        await api.post(`/order/${orderId}/payment`, payload);
        await getEventFractionls();
        resetForm();
        onToggle();
      } catch (error) {
        const err = error as AxiosError;
        toast.error(err.message);
      } finally {
        setState(States.default);
      }
    }
  };

  useEffect(() => {
    getEventFractionls();
  }, []);
  return (
    <>
      <Loading isVisible={state === 'loading'} />
      <EventFractionalReportUI
        title={title}
        visible={visible}
        onToggle={onToggle}
        shouldShowModal={shouldShowModal}
        report={report}
        onShouldShowModal={handleOnShouldShowModal}
        selectedOrder={selectedOrder}
        formData={formData}
        formErrors={formErrors}
        onChangeFormInput={onChangeFormInput}
        onCreatePayment={handleOnCreatePayment}
      />
    </>
  );
};
